import { Link } from 'react-router-dom';
import { capitalize } from '@/lib/collections';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Plus } from 'lucide-react';
import { PictoVoirDetail } from '@/assets/icons-april-fr/pictos';
import { PictoDeclarationSinistre } from '@/assets/icons-april-fr/pictos';
import useIsPWA from '@/hooks/use-is-pwa';
import { useIsFleetUser } from '@/hooks/api/use-user';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { useTiersAccidentsData } from '@/hooks/api/use-accidents';
import { isACLContract, isWakamContract } from '@/lib/contracts';
import { formatDate } from '@/lib/dates';

type BadgeVariant = 'warning' | 'destructive' | 'secondary';

const getStatusVariant = (situationCode?: string): BadgeVariant => {
    switch (situationCode) {
        case '1': return 'warning';
        case '2':
        case '8': return 'destructive';
        case 'A': return 'warning';
        case '3': return 'secondary';
        default: return 'secondary';
    }
};

const ListeSinistres = () => {
    const accidents = useTiersAccidentsData();
    const contract = useSelectedContract();
    const isACL = isACLContract(contract);
    const isWakam = isWakamContract(contract);
    const isFlotte = useIsFleetUser();
    const isPWA = useIsPWA();

    return (
        <div className="space-y-6">
            {!isACL && !isWakam && !isFlotte && (
                <div className="flex justify-start">
                    <Button asChild className="!text-white transition-all duration-300 ease-in-out cursor-pointer">
                        <Link to="/mes-sinistres/je-declare-un-sinistre">
                            <Plus className="w-4 h-4" aria-hidden="true" />
                            Déclarer un sinistre
                        </Link>
                    </Button>
                </div>
            )}

            {/* Sinistres Grid */}
            {accidents.length >= 1 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {accidents.map((c) => (
                        <Card key={c.id} className="transition-all duration-200 hover:shadow-lg flex flex-col overflow-hidden">
                            <CardHeader className="overflow-hidden">
                                <div className="flex flex-wrap items-start justify-between gap-2 overflow-hidden">
                                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                        <PictoDeclarationSinistre className="w-6 h-6 shrink-0" aria-hidden="true" />
                                        <div className="min-w-0">
                                            <h2 className="font-semibold text-sm sm:text-base break-words">Sinistre N°{c.numCbt}</h2>
                                            {c.libelle && (
                                                <p className="text-xs sm:text-sm text-muted-foreground break-words">{c.libelle}</p>
                                            )}
                                        </div>
                                    </div>
                                    <Badge variant={getStatusVariant(c.situationCode)} className={isPWA ? "shrink-0 text-sm px-4 py-1.5 whitespace-nowrap" : "shrink-0 text-[10px] sm:text-xs whitespace-nowrap"}>
                                        {capitalize(c.situation)}
                                    </Badge>
                                </div>
                            </CardHeader>

                            <CardContent className="flex-1">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                                    <div>
                                        <p className="text-muted-foreground">Date de survenance</p>
                                        <p className="font-medium">{formatDate(c.dateSurv)}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Catégorie</p>
                                        <p className="font-medium">{c.categorie || '—'}</p>
                                    </div>
                                </div>
                            </CardContent>

                            {/* Voir le détail */}
                            <div className="px-6 py-6 border-t mt-auto">
                                <Button asChild variant="outline" className="w-full cursor-pointer">
                                    <Link to={`/mes-sinistres/contrat/${c.id_contrat}/sinistre/${c.id}`}>
                                        <PictoVoirDetail className="w-4 h-4" aria-hidden="true" />
                                        Voir le détail
                                    </Link>
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="flex items-center justify-center h-12 w-12 rounded-full bg-muted mb-6">
                            <AlertTriangle className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
                        </div>
                        <p className="text-muted-foreground">Vous n'avez actuellement aucun sinistre déclaré.</p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default ListeSinistres;
